import React, { useState } from 'react';
import QuantLibExamples from './QuantLibExamples';
import MLExamples from './MLExamples';
import AIVariations from './AIVariations';
import LargeFileOptimization from './LargeFileOptimization';

const ArtemisApproaches = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [expandedBranches, setExpandedBranches] = useState({});
  const [expandedPRs, setExpandedPRs] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  // Task descriptions for each example type
  const taskDescriptions = {
    software: "Optimize a data processing function that handles millions of records",
    ml: "Build a customer churn prediction model for a SaaS company",
    trading: "Create a high-frequency trading strategy for cryptocurrency arbitrage",
    data: "Design a real-time data pipeline for IoT sensor data processing"
  };

  // Toggle branch expansion
  const toggleBranch = (branchId) => {
    setExpandedBranches(prev => ({
      ...prev,
      [branchId]: !prev[branchId]
    }));
  };

  // Toggle PR expansion
  const togglePR = (prId) => {
    setExpandedPRs(prev => ({
      ...prev,
      [prId]: !prev[prId]
    }));
  };

  // Modal component
  const Modal = ({ isOpen, onClose, content }) => {
    if (!isOpen) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '16px',
          padding: '30px',
          maxWidth: '800px',
          maxHeight: '80vh',
          overflow: 'auto',
          position: 'relative',
          border: '2px solid #4ECDC4'
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: '#4ECDC4',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              fontSize: '18px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ✕
          </button>
          {content}
        </div>
      </div>
    );
  };

  // Single Code Evolution content for modal
  const SingleCodeEvolutionContent = ({ onClose }) => (
    <div style={{ color: 'white' }}>
      <h3 style={{ 
        margin: '0 0 20px 0', 
        fontSize: '24px',
        color: '#4ECDC4'
      }}>
        🧬 Artemis Intelligence: Single File Evolution
      </h3>
      
      <div style={{
        background: 'rgba(78, 205, 196, 0.1)',
        padding: '20px',
        borderRadius: '10px',
        marginBottom: '20px',
        border: '1px solid rgba(78, 205, 196, 0.3)'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#4ECDC4' }}>api.py Variations</h4>
        <p style={{ fontSize: '14px', opacity: 0.9, marginBottom: '15px' }}>
          Artemis Intelligence generates multiple optimized versions of your code:
        </p>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {[
            { 
              version: 'Variation 1: Performance Focus',
              changes: ['Async/await optimization', 'Database connection pooling', 'Caching layer'],
              score: 95,
              metrics: { speed: '+45%', memory: '-20%', readability: '85%' }
            },
            { 
              version: 'Variation 2: Memory Optimization',
              changes: ['Generator functions', 'Lazy loading', 'Stream processing'],
              score: 88,
              metrics: { speed: '+20%', memory: '-60%', readability: '90%' }
            },
            { 
              version: 'Variation 3: Maintainability Focus',
              changes: ['Clear abstractions', 'Better error handling', 'Comprehensive logging'],
              score: 82,
              metrics: { speed: '+10%', memory: '-5%', readability: '98%' }
            }
          ].map((variant, i) => (
            <div key={i} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px'
              }}>
                <h5 style={{ margin: 0, color: '#96CEB4' }}>{variant.version}</h5>
                <span style={{
                  background: 'rgba(78, 205, 196, 0.2)',
                  padding: '4px 12px',
                  borderRadius: '15px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4ECDC4'
                }}>
                  Score: {variant.score}/100
                </span>
              </div>
              
              <div style={{ fontSize: '13px', marginBottom: '10px' }}>
                <strong>Changes:</strong>
                <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                  {variant.changes.map((change, idx) => (
                    <li key={idx} style={{ marginBottom: '3px' }}>{change}</li>
                  ))}
                </ul>
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: '15px',
                fontSize: '12px',
                opacity: 0.8
              }}>
                <span>Speed: <strong style={{ color: '#4ECDC4' }}>{variant.metrics.speed}</strong></span>
                <span>Memory: <strong style={{ color: '#96CEB4' }}>{variant.metrics.memory}</strong></span>
                <span>Readability: <strong style={{ color: '#DDA0DD' }}>{variant.metrics.readability}</strong></span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div style={{
        background: 'rgba(244, 81, 30, 0.1)',
        padding: '15px',
        borderRadius: '10px',
        border: '1px solid rgba(244, 81, 30, 0.3)'
      }}>
        <p style={{ margin: 0, fontSize: '14px', textAlign: 'center' }}>
          <strong>Learn more:</strong> View the complete{' '}
          <button
            style={{ 
              color: '#F4511E', 
              textDecoration: 'underline',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px'
            }}
            onClick={() => {
              // Navigate to single code evolution in ArtemisUniqueness
              window.location.hash = '#single-code-evolution';
              onClose();
            }}
          >
            Single Code Evolution workflow
          </button>
        </p>
      </div>
    </div>
  );

  // PR Result Component
  const PRResult = ({ approach, score, description }) => (
    <div style={{
      background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.1), rgba(150, 206, 180, 0.1))',
      padding: '20px',
      borderRadius: '10px',
      border: '1px solid rgba(78, 205, 196, 0.3)',
      marginTop: '20px'
    }}>
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px',
          marginBottom: expandedPRs[approach] ? '15px' : '0',
          cursor: 'pointer'
        }}
        onClick={() => togglePR(approach)}
      >
        <span style={{ fontSize: '24px' }}>🔀</span>
        <h4 style={{ 
          margin: 0, 
          fontSize: '18px',
          color: '#4ECDC4',
          flex: 1
        }}>
          Pull Request Generated
        </h4>
        <span style={{ 
          background: 'rgba(78, 205, 196, 0.2)',
          padding: '3px 10px',
          borderRadius: '12px',
          color: '#4ECDC4',
          fontWeight: '600',
          fontSize: '14px'
        }}>
          {score}/100
        </span>
        <span style={{ fontSize: '12px', opacity: 0.7 }}>
          {expandedPRs[approach] ? '▼' : '▶'}
        </span>
      </div>
      
      {expandedPRs[approach] && (
        <>
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '15px',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '13px'
          }}>
            <div style={{ marginBottom: '10px' }}>
              <strong style={{ color: '#96CEB4' }}>PR Title:</strong> {description.title}
            </div>
            <div style={{ marginBottom: '10px' }}>
              <strong style={{ color: '#96CEB4' }}>Score:</strong> 
              <span style={{ 
                marginLeft: '10px',
                background: 'rgba(78, 205, 196, 0.2)',
                padding: '3px 10px',
                borderRadius: '12px',
                color: '#4ECDC4',
                fontWeight: '600'
              }}>
                {score}/100
              </span>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <strong style={{ color: '#96CEB4' }}>Changes:</strong>
              <ul style={{ margin: '5px 0 0 20px', paddingLeft: '10px' }}>
                {description.changes.map((change, i) => (
                  <li key={i} style={{ marginBottom: '3px', color: '#ddd' }}>{change}</li>
                ))}
              </ul>
            </div>
            <div>
              <strong style={{ color: '#96CEB4' }}>Metrics:</strong>
              <div style={{ 
                display: 'flex', 
                gap: '20px',
                marginTop: '5px',
                fontSize: '12px',
                flexWrap: 'wrap'
              }}>
                {Object.entries(description.metrics).map(([key, value]) => (
                  <span key={key}>
                    {key}: <strong style={{ color: '#4ECDC4' }}>{value}</strong>
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          <div style={{
            marginTop: '15px',
            padding: '10px',
            background: 'rgba(78, 205, 196, 0.1)',
            borderRadius: '6px',
            fontSize: '13px',
            textAlign: 'center'
          }}>
            <strong>Artemis Analysis:</strong> {description.analysis}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)',
      color: 'white',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      padding: '40px 20px',
      fontSize: '16px'
    }}>
      <div style={{ maxWidth: '1800px', margin: '0 auto' }}>
        <h1 style={{ 
          margin: '0 0 15px 0', 
          fontSize: '42px',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #F4511E, #FF6D00)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Artemis Evolution: Three Approaches
        </h1>
        <p style={{ margin: '0 0 30px 0', fontSize: '20px', opacity: 0.8 }}>
          From single agent to multi-agent AI systems
        </p>
        
        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '20px',
          marginBottom: '40px',
          borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
          paddingBottom: '0'
        }}>
          <button
            onClick={() => setActiveTab('overview')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'overview' ? '#4ECDC4' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'overview' ? '3px solid #4ECDC4' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('quantlib')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'quantlib' ? '#F4511E' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'quantlib' ? '3px solid #F4511E' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            QuantLib Example
          </button>
          <button
            onClick={() => setActiveTab('ml')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'ml' ? '#96CEB4' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'ml' ? '3px solid #96CEB4' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            ML Example
          </button>
          <button
            onClick={() => setActiveTab('intelligence')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'intelligence' ? '#DDA0DD' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'intelligence' ? '3px solid #DDA0DD' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            Artemis Intelligence
          </button>
          <button
            onClick={() => setActiveTab('largefile')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'largefile' ? '#F4511E' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'largefile' ? '3px solid #F4511E' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            Large File Evolution
          </button>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'overview' ? (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(550px, 1fr))',
              gap: '40px',
              marginBottom: '50px'
            }}>
          {/* Approach 1: Single Agent */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            padding: '40px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.3)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{
              position: 'absolute',
              top: '-15px',
              left: '30px',
              background: '#0F2027',
              padding: '8px 20px',
              borderRadius: '20px',
              fontSize: '16px',
              fontWeight: '600',
              color: '#FF6B6B',
              border: '2px solid #FF6B6B'
            }}>
              Approach 1: Basic
            </div>
            
            <h3 style={{ margin: '30px 0 25px 0', fontSize: '24px' }}>
              🤖 Agent "optimize my code"
            </h3>
            
            {/* Git-style visualization for Approach 1 */}
            <div style={{
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '12px',
              padding: '30px',
              margin: '35px 0',
              fontFamily: 'monospace',
              fontSize: '14px'
            }}>
              {/* Main branch */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: '#FF6B6B',
                  marginRight: '12px'
                }}></div>
                <div style={{ color: '#FF6B6B', fontSize: '16px' }}>main</div>
              </div>

              {/* Single branch creation - collapsible */}
              <div style={{ marginLeft: '6px', borderLeft: '2px solid #FF6B6B', paddingLeft: '25px' }}>
                <div 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '15px',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleBranch('approach1-branch')}
                >
                  <div style={{ 
                    width: '2px', 
                    height: '25px', 
                    background: '#666',
                    position: 'absolute',
                    marginLeft: '-27px'
                  }}></div>
                  <div style={{
                    background: 'rgba(255, 107, 107, 0.2)',
                    padding: '12px 18px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 107, 107, 0.5)',
                    width: '100%',
                    fontSize: '15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>🌿 agent-optimization (5 files)</span>
                    <span style={{ fontSize: '12px', opacity: 0.7 }}>
                      {expandedBranches['approach1-branch'] ? '▼' : '▶'}
                    </span>
                  </div>
                </div>

                {/* Collapsible content */}
                {expandedBranches['approach1-branch'] && (
                  <>
                    {/* Simple changes with tree structure */}
                    <div style={{ 
                      marginLeft: '25px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '20px' }}>
                        <strong>📝 Agent makes direct changes:</strong>
                      </div>
                      
                      {/* File tree structure */}
                      <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                        {[
                          { 
                            file: 'api.py', 
                            icon: '📄',
                            change: 'Refactored endpoints',
                            details: 'Basic performance improvements'
                          },
                          { 
                            file: 'database.py', 
                            icon: '🗄️',
                            change: 'Optimized queries',
                            details: 'Added simple indexing'
                          },
                          { 
                            file: 'utils.py', 
                            icon: '🔧',
                            change: 'Improved helpers',
                            details: 'Removed redundant code'
                          },
                          { 
                            file: 'config.py', 
                            icon: '⚙️',
                            change: 'Updated settings',
                            details: 'Production configurations'
                          },
                          { 
                            file: 'main.py', 
                            icon: '🚀',
                            change: 'Performance tweaks',
                            details: 'Entry point optimization'
                          }
                        ].map((item, i) => (
                          <div key={i} style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '12px',
                            position: 'relative'
                          }}>
                            {/* Tree branch line */}
                            <div style={{
                              position: 'absolute',
                              left: '-20px',
                              top: '0',
                              bottom: i === 4 ? '50%' : '0',
                              width: '1px',
                              background: 'rgba(255, 255, 255, 0.2)'
                            }}></div>
                            <div style={{
                              position: 'absolute',
                              left: '-20px',
                              top: '50%',
                              width: '20px',
                              height: '1px',
                              background: 'rgba(255, 255, 255, 0.2)'
                            }}></div>
                            
                            {/* File box */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              background: 'rgba(255, 107, 107, 0.15)',
                              padding: '8px 16px',
                              borderRadius: '6px',
                              border: '1px solid rgba(255, 107, 107, 0.4)',
                              flex: 1
                            }}>
                              <span>{item.icon}</span>
                              <span style={{ color: '#FF9999', fontWeight: '500', minWidth: '80px' }}>
                                {item.file}
                              </span>
                              <span style={{ color: '#FF6B6B' }}>→</span>
                              <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>
                                {item.change}
                              </span>
                              <span style={{ 
                                color: 'rgba(255, 255, 255, 0.5)', 
                                fontSize: '12px',
                                marginLeft: 'auto'
                              }}>
                                {item.details}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Direct result */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      marginTop: '20px'
                    }}>
                      <div style={{ 
                        width: '20px', 
                        height: '2px', 
                        background: '#FF6B6B',
                        position: 'absolute',
                        left: '4px'
                      }}></div>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: '#FF6B6B',
                        position: 'absolute',
                        left: '-6px'
                      }}></div>
                      <div style={{
                        background: 'rgba(255, 107, 107, 0.2)',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 107, 107, 0.5)',
                        fontSize: '14px',
                        marginLeft: '25px'
                      }}>
                        ✅ Single solution - No alternatives
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>



            {/* PR Result */}
            <PRResult 
              approach="basic"
              score={75}
              description={{
                title: "feat: Basic code optimization",
                changes: [
                  "Refactored API endpoints for better performance",
                  "Optimized database queries with indexing",
                  "Updated configuration for production"
                ],
                metrics: {
                  Performance: "+15%",
                  "Code Quality": "B+",
                  Coverage: "72%"
                },
                analysis: "Single-pass optimization with limited scope. No alternative solutions explored."
              }}
            />
            
            {/* Pros/Cons */}
            <div style={{ fontSize: '16px', marginTop: '35px' }}>
              <div style={{ marginBottom: '20px' }}>
                <strong style={{ color: '#4ECDC4', fontSize: '18px' }}>✓ Pros:</strong>
                <ul style={{ margin: '10px 0', paddingLeft: '28px', lineHeight: '2' }}>
                  <li>Simple & fast</li>
                  <li>Low cost</li>
                  <li>Quick iteration</li>
                </ul>
              </div>
              <div>
                <strong style={{ color: '#FF6B6B', fontSize: '18px' }}>✗ Cons:</strong>
                <ul style={{ margin: '10px 0', paddingLeft: '28px', lineHeight: '2' }}>
                  <li>No alternatives</li>
                  <li>Limited optimization</li>
                  <li>No user preferences</li>
                  <li>Can hallucinate solutions</li>
                  <li>May introduce technical debt</li>
                  <li>Inconsistent results</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Approach 2: Current Artemis */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            padding: '40px',
            border: '1px solid rgba(78, 205, 196, 0.3)',
            position: 'relative',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(78, 205, 196, 0.2)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{
              position: 'absolute',
              top: '-15px',
              left: '30px',
              background: '#0F2027',
              padding: '8px 20px',
              borderRadius: '20px',
              fontSize: '16px',
              fontWeight: '600',
              color: '#4ECDC4',
              border: '2px solid #4ECDC4'
            }}>
              Approach 2: Current
            </div>
            
            <h3 style={{ margin: '30px 0 25px 0', fontSize: '24px' }}>
              🚀 Artemis "optimize my code"
            </h3>
            
            {/* Git-like branching visualization */}
            <div style={{
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '12px',
              padding: '30px',
              margin: '35px 0',
              fontFamily: 'monospace',
              fontSize: '14px'
            }}>
              {/* Main branch */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: '#4ECDC4',
                  marginRight: '12px'
                }}></div>
                <div style={{ color: '#4ECDC4', fontSize: '16px' }}>main</div>
              </div>

              {/* Branch creation - collapsible */}
              <div style={{ marginLeft: '6px', borderLeft: '2px solid #4ECDC4', paddingLeft: '25px' }}>
                {/* Initial branch */}
                <div 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '15px',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleBranch('approach2-branch')}
                >
                  <div style={{ 
                    width: '2px', 
                    height: '25px', 
                    background: '#666',
                    position: 'absolute',
                    marginLeft: '-27px'
                  }}></div>
                  <div style={{
                    background: 'rgba(150, 206, 180, 0.2)',
                    padding: '12px 18px',
                    borderRadius: '8px',
                    border: '1px solid rgba(150, 206, 180, 0.5)',
                    marginBottom: '8px',
                    width: '100%',
                    fontSize: '15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>🌿 artemis-optimization (8 files)</span>
                    <span style={{ fontSize: '12px', opacity: 0.7 }}>
                      {expandedBranches['approach2-branch'] ? '▼' : '▶'}
                    </span>
                  </div>
                </div>

                {expandedBranches['approach2-branch'] && (
                  <>
                    {/* File versions with sources */}
                    <div style={{ 
                      marginLeft: '25px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '20px' }}>
                        <strong>📝 Code diff suggestions from multiple sources:</strong>
                      </div>
                      
                      {/* File tree structure */}
                      <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                        {[
                          { 
                            file: 'api.py', 
                            icon: '📄',
                            versions: [
                              { v: 'v1', source: 'LLM', score: 75, color: '#FF6B6B', description: 'Basic optimization' },
                              { v: 'v2', source: 'Human', score: 85, color: '#4ECDC4', description: 'Performance focused' },
                              { v: 'v3', source: 'Agent', score: 80, color: '#96CEB4', description: 'Balanced approach' }
                            ]
                          },
                          { 
                            file: 'database.py', 
                            icon: '🗄️',
                            versions: [
                              { v: 'v1', source: 'LLM', score: 90, color: '#FF6B6B', description: 'Query optimization' },
                              { v: 'v2', source: 'Human', score: 88, color: '#4ECDC4', description: 'Connection pooling' },
                              { v: 'v3', source: 'Agent', score: 92, color: '#96CEB4', description: 'Full refactor' }
                            ]
                          }
                        ].map((item, i) => (
                          <div key={i} style={{ marginBottom: '25px' }}>
                            {/* File header */}
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              marginBottom: '12px',
                              fontSize: '16px',
                              fontWeight: '600'
                            }}>
                              <span style={{ marginRight: '8px' }}>{item.icon}</span>
                              <span style={{ color: '#96CEB4' }}>{item.file}</span>
                              {item.file === 'api.py' && (
                                <button
                                  onClick={() => setActiveTab('intelligence')}
                                  style={{
                                    marginLeft: '15px',
                                    background: 'rgba(78, 205, 196, 0.2)',
                                    border: '1px solid rgba(78, 205, 196, 0.4)',
                                    borderRadius: '4px',
                                    padding: '4px 12px',
                                    fontSize: '12px',
                                    color: '#4ECDC4',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                  }}
                                >
                                  <span>🧬</span> View AI variations
                                </button>
                              )}
                            </div>
                            
                            {/* Version tree */}
                            <div style={{ marginLeft: '20px' }}>
                              {item.versions.map((ver, idx) => (
                                <div key={idx} style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginBottom: '10px',
                                  position: 'relative'
                                }}>
                                  {/* Tree branch line */}
                                  <div style={{
                                    position: 'absolute',
                                    left: '-20px',
                                    top: '0',
                                    bottom: idx === item.versions.length - 1 ? '50%' : '0',
                                    width: '1px',
                                    background: 'rgba(255, 255, 255, 0.2)'
                                  }}></div>
                                  <div style={{
                                    position: 'absolute',
                                    left: '-20px',
                                    top: '50%',
                                    width: '20px',
                                    height: '1px',
                                    background: 'rgba(255, 255, 255, 0.2)'
                                  }}></div>
                                  
                                  {/* Version box */}
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    background: `${ver.color}15`,
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: `1px solid ${ver.color}40`,
                                    flex: 1,
                                    transition: 'all 0.2s ease',
                                    cursor: item.file === 'api.py' ? 'pointer' : 'default'
                                  }}
                                  onClick={() => {
                                    if (item.file === 'api.py') {
                                      setActiveTab('intelligence');
                                    }
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.background = `${ver.color}25`;
                                    e.currentTarget.style.transform = 'translateX(5px)';
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.background = `${ver.color}15`;
                                    e.currentTarget.style.transform = 'translateX(0)';
                                  }}>
                                    {/* Version tag */}
                                    <span style={{
                                      background: ver.color,
                                      color: 'white',
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      minWidth: '35px',
                                      textAlign: 'center'
                                    }}>
                                      {ver.v}
                                    </span>
                                    
                                    {/* Source */}
                                    <span style={{
                                      color: ver.color,
                                      fontWeight: '500',
                                      minWidth: '60px'
                                    }}>
                                      {ver.source}
                                    </span>
                                    
                                    {/* Description */}
                                    <span style={{
                                      color: 'rgba(255, 255, 255, 0.7)',
                                      fontSize: '13px',
                                      flex: 1
                                    }}>
                                      {ver.description}
                                    </span>
                                    
                                    {/* Score */}
                                    <span style={{
                                      background: 'rgba(255, 255, 255, 0.1)',
                                      padding: '4px 10px',
                                      borderRadius: '12px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      color: ver.color
                                    }}>
                                      {ver.score}/100
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Legend */}
                      <div style={{
                        marginTop: '25px',
                        paddingTop: '20px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        gap: '20px',
                        fontSize: '12px',
                        opacity: 0.7
                      }}>
                        <span><span style={{ color: '#FF6B6B' }}>●</span> LLM suggestions</span>
                        <span><span style={{ color: '#4ECDC4' }}>●</span> Human feedback</span>
                        <span><span style={{ color: '#96CEB4' }}>●</span> Agent improvements</span>
                      </div>
                    </div>

                    {/* Artemis Intelligence Evolution */}
                    <div style={{
                      background: 'rgba(78, 205, 196, 0.1)',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      border: '1px solid rgba(78, 205, 196, 0.3)'
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#4ECDC4' }}>
                        🧬 Artemis Intelligence - Evolutionary Algorithm
                      </div>
                      <div style={{ fontSize: '13px', lineHeight: '1.8', opacity: 0.9 }}>
                        • Auto-scores all code diff suggestions<br/>
                        • <strong>Generates multiple variations per file</strong> 
                        <button
                          onClick={() => {
                            setModalContent(<SingleCodeEvolutionContent onClose={() => setShowModal(false)} />);
                            setShowModal(true);
                          }}
                          style={{
                            marginLeft: '8px',
                            background: 'none',
                            border: 'none',
                            color: '#4ECDC4',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          (see example)
                        </button><br/>
                        • Creates combination branches for testing<br/>
                        • Evolves solutions based on performance metrics<br/>
                        • Generates new suggestions through mutation
                      </div>
                    </div>

                    {/* Evolution branches */}
                    <div style={{ fontSize: '13px', opacity: 0.8, marginBottom: '12px' }}>
                      Evolution creates and tests combinations:
                    </div>
                    
                    <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                      {[
                        { name: 'evolution-1', combo: 'api_v1, db_v1', score: 78, color: '#FF6B6B' },
                        { name: 'evolution-2', combo: 'api_v2, db_v1', score: 85, color: '#96CEB4' },
                        { name: 'evolution-3', combo: 'api_v3, db_v2', score: 82, color: '#DDA0DD' },
                        { name: 'evolution-4', combo: 'api_v2, db_v3', score: 92, color: '#4ECDC4' }
                      ].map((branch, i) => (
                        <div key={i} style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          marginBottom: '10px',
                          position: 'relative'
                        }}>
                          {/* Tree branch line */}
                          <div style={{
                            position: 'absolute',
                            left: '-20px',
                            top: '0',
                            bottom: i === 3 ? '50%' : '0',
                            width: '1px',
                            background: 'rgba(255, 255, 255, 0.2)'
                          }}></div>
                          <div style={{ 
                            width: '20px', 
                            height: '1px', 
                            background: 'rgba(255, 255, 255, 0.2)',
                            position: 'absolute',
                            left: '-20px',
                            top: '50%'
                          }}></div>
                          
                          {/* Branch box */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            background: branch.score === 92 
                              ? `${branch.color}25` 
                              : `${branch.color}15`,
                            padding: '10px 15px',
                            borderRadius: '6px',
                            border: `1px solid ${branch.score === 92 
                              ? `${branch.color}60` 
                              : `${branch.color}30`}`,
                            flex: 1,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.transform = 'translateX(5px)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.transform = 'translateX(0)';
                          }}>
                            <div style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              background: branch.color,
                              flexShrink: 0
                            }}></div>
                            <span style={{ 
                              color: branch.color,
                              fontWeight: branch.score === 92 ? '600' : '500',
                              minWidth: '90px'
                            }}>
                              {branch.name}
                            </span>
                            <span style={{ 
                              color: 'rgba(255, 255, 255, 0.6)', 
                              fontSize: '13px',
                              flex: 1
                            }}>
                              {branch.combo}
                            </span>
                            <span style={{ 
                              background: branch.score === 92 
                                ? `${branch.color}30`
                                : 'rgba(255, 255, 255, 0.1)',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: branch.score === 92 ? 'white' : branch.color
                            }}>
                              {branch.score}/100
                            </span>
                            {branch.score === 92 && (
                              <span style={{
                                background: branch.color,
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                marginLeft: '8px'
                              }}>
                                BEST
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Merge back */}
                    <div style={{ 
                      marginTop: '20px',
                      paddingTop: '20px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ 
                          width: '20px', 
                          height: '2px', 
                          background: '#4ECDC4',
                          position: 'absolute',
                          left: '4px'
                        }}></div>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: '#4ECDC4',
                          position: 'absolute',
                          left: '-6px'
                        }}></div>
                        <div style={{
                          background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.2), rgba(150, 206, 180, 0.2))',
                          padding: '12px 20px',
                          borderRadius: '8px',
                          border: '1px solid rgba(78, 205, 196, 0.5)',
                          fontSize: '14px',
                          marginLeft: '25px',
                          fontWeight: '600'
                        }}>
                          ✅ Merged: evolution-4 → main (Score: 92/100)
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>



            {/* PR Result */}
            <PRResult 
              approach="artemis"
              score={92}
              description={{
                title: "feat: AI-driven performance optimization with evolutionary selection",
                changes: [
                  "Implemented async/await patterns in API endpoints (+45% speed)",
                  "Added connection pooling for database operations",
                  "Optimized memory usage with generator functions",
                  "Enhanced error handling and logging",
                  "Added comprehensive caching layer"
                ],
                metrics: {
                  Performance: "+45%",
                  Memory: "-35%",
                  "Code Quality": "A",
                  Coverage: "94%",
                  Readability: "92%"
                },
                analysis: "Best solution selected from 4 evolutionary branches. Combines performance optimization from v2 with memory efficiency techniques. All changes validated through automated testing."
              }}
            />
            
            {/* Pros/Cons */}
            <div style={{ fontSize: '16px', marginTop: '35px' }}>
              <div style={{ marginBottom: '20px' }}>
                <strong style={{ color: '#4ECDC4', fontSize: '18px' }}>✓ Pros:</strong>
                <ul style={{ margin: '10px 0', paddingLeft: '28px', lineHeight: '2' }}>
                  <li>Multiple suggestion sources</li>
                  <li>Evolutionary improvement</li>
                  <li>Auto-scoring & optimization</li>
                  <li>Transparent process</li>
                </ul>
              </div>
              <div>
                <strong style={{ color: '#FF6B6B', fontSize: '18px' }}>✗ Cons:</strong>
                <ul style={{ margin: '10px 0', paddingLeft: '28px', lineHeight: '2' }}>
                  <li>Single agent perspective</li>
                  <li>Longer processing</li>
                  <li>Complex setup</li>
                  <li>Agents don't always work reliably</li>
                  <li>Can still hallucinate</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Approach 3: Multi-Agent Artemis */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(244, 81, 30, 0.1), rgba(255, 109, 0, 0.1))',
            borderRadius: '16px',
            padding: '40px',
            border: '2px solid rgba(244, 81, 30, 0.5)',
            position: 'relative',
            boxShadow: '0 5px 20px rgba(244, 81, 30, 0.2)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 15px 40px rgba(244, 81, 30, 0.3)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 5px 20px rgba(244, 81, 30, 0.2)';
          }}>
            <div style={{
              position: 'absolute',
              top: '-15px',
              left: '30px',
              background: 'linear-gradient(135deg, #F4511E, #FF6D00)',
              padding: '8px 20px',
              borderRadius: '20px',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              Approach 3: 🔥
              <span style={{
                background: 'rgba(255, 255, 255, 0.2)',
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '14px',
                animation: 'pulse 2s infinite'
              }}>
                In Progress
              </span>
            </div>
            
            <h3 style={{ margin: '30px 0 25px 0', fontSize: '24px' }}>
              🚀 Artemis "optimize my code"
            </h3>
            
            {/* Git-style visualization for multi-agent */}
            <div style={{
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '12px',
              padding: '30px',
              margin: '35px 0',
              fontFamily: 'monospace',
              fontSize: '14px',
              position: 'relative'
            }}>
              {/* Main branch */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: '#F4511E',
                  marginRight: '12px'
                }}></div>
                <div style={{ color: '#F4511E', fontSize: '16px' }}>main</div>
              </div>

              {/* Multiple agent branches - First iteration */}
              <div style={{ marginLeft: '6px', borderLeft: '2px solid #F4511E', paddingLeft: '25px' }}>
                <div style={{
                  background: 'rgba(244, 81, 30, 0.1)',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid rgba(244, 81, 30, 0.3)'
                }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#F4511E' }}>
                    📢 Initial Request: "Optimize my code"
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.9 }}>
                    Multiple AI agents work independently on the same task
                  </div>
                </div>

                {[
                  { name: 'GPT-4', icon: '🤖', color: '#FF6B6B', files: 12, score: 85, approach: 'Functional refactoring' },
                  { name: 'Claude', icon: '🧠', color: '#4ECDC4', files: 10, score: 88, approach: 'Architecture redesign' },
                  { name: 'Gemini', icon: '⚡', color: '#96CEB4', files: 15, score: 82, approach: 'Performance optimization' },
                  { name: 'Llama', icon: '🦙', color: '#DDA0DD', files: 8, score: 90, approach: 'Code simplification' }
                ].map((agent, i) => (
                  <div key={i} style={{ marginBottom: '25px' }}>
                    <div 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        marginBottom: '10px',
                        position: 'relative',
                        cursor: 'pointer'
                      }}
                      onClick={() => toggleBranch(`agent-${agent.name}`)}
                    >
                      <div style={{ 
                        width: '20px', 
                        height: '2px', 
                        background: agent.color,
                        position: 'absolute',
                        left: '-27px'
                      }}></div>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: agent.color,
                        position: 'absolute',
                        left: '-32px'
                      }}></div>
                      <div style={{
                        background: `${agent.color}20`,
                        padding: '12px 18px',
                        borderRadius: '8px',
                        border: `1px solid ${agent.color}50`,
                        fontSize: '13px',
                        width: '100%'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <span style={{ fontSize: '18px', marginRight: '10px' }}>{agent.icon}</span>
                            <span style={{ color: agent.color, fontWeight: '600' }}>{agent.name}-branch</span>
                            <span style={{ color: '#888', marginLeft: '12px' }}>({agent.files} files)</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ color: agent.color, fontWeight: '600', fontSize: '15px' }}>
                              {agent.score}/100
                            </span>
                            <span style={{ fontSize: '12px', opacity: 0.7 }}>
                              {expandedBranches[`agent-${agent.name}`] ? '▼' : '▶'}
                            </span>
                          </div>
                        </div>
                        {!expandedBranches[`agent-${agent.name}`] && (
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#aaa', 
                            marginTop: '6px',
                            fontStyle: 'italic'
                          }}>
                            {agent.approach}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {expandedBranches[`agent-${agent.name}`] && (
                      <div style={{
                        marginLeft: '30px',
                        padding: '15px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        fontSize: '13px'
                      }}>
                        <div style={{ marginBottom: '10px' }}>
                          <strong style={{ color: agent.color }}>Approach:</strong> {agent.approach}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <strong style={{ color: agent.color }}>Key Changes:</strong>
                          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                            {agent.name === 'GPT-4' && (
                              <>
                                <li>Functional programming patterns</li>
                                <li>Immutable data structures</li>
                                <li>Pure function refactoring</li>
                              </>
                            )}
                            {agent.name === 'Claude' && (
                              <>
                                <li>Microservices architecture</li>
                                <li>Event-driven design</li>
                                <li>Domain-driven structure</li>
                              </>
                            )}
                            {agent.name === 'Gemini' && (
                              <>
                                <li>Parallel processing</li>
                                <li>Cache optimization</li>
                                <li>Algorithm improvements</li>
                              </>
                            )}
                            {agent.name === 'Llama' && (
                              <>
                                <li>Code deduplication</li>
                                <li>Simplified abstractions</li>
                                <li>Reduced complexity</li>
                              </>
                            )}
                          </ul>
                        </div>
                        <div>
                          <strong style={{ color: agent.color }}>Metrics:</strong>
                          <div style={{ display: 'flex', gap: '15px', marginTop: '5px', fontSize: '12px' }}>
                            <span>Speed: <strong>+{20 + i * 5}%</strong></span>
                            <span>Memory: <strong>-{15 + i * 3}%</strong></span>
                            <span>Complexity: <strong>-{10 + i * 2}%</strong></span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Artemis review and feedback loop */}
                <div style={{
                  background: 'rgba(244, 81, 30, 0.2)',
                  padding: '20px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid rgba(244, 81, 30, 0.5)'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '12px', color: '#F4511E', fontSize: '15px' }}>
                    🔄 Artemis Iterative Review Process
                  </div>
                  <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                    <strong>Round 1:</strong> Initial review & feedback<br/>
                    • Analyzes each agent's approach<br/>
                    • Identifies strengths and weaknesses<br/>
                    • Provides specific improvement feedback<br/>
                    <br/>
                    <strong>Round 2:</strong> Agents refine based on feedback<br/>
                    • Re-asks agents with targeted improvements<br/>
                    • Encourages cross-pollination of ideas<br/>
                    <br/>
                    <strong>Round 3:</strong> Final evolution<br/>
                    • Combines best elements from all approaches<br/>
                    • Creates hybrid solution
                  </div>
                </div>

                {/* In Progress indicator */}
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  opacity: 0.8
                }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: 'rgba(244, 81, 30, 0.1)',
                    padding: '12px 24px',
                    borderRadius: '30px',
                    border: '1px solid rgba(244, 81, 30, 0.3)'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#F4511E',
                      animation: 'pulse 1.5s infinite'
                    }}></div>
                    <span style={{ fontSize: '14px', color: '#F4511E' }}>
                      Evolution in progress...
                    </span>
                  </div>
                </div>
              </div>
            </div>



            {/* PR Result - Preview */}
            <PRResult 
              approach="multi-agent"
              score={96}
              description={{
                title: "feat: Multi-agent optimized solution with cross-model insights",
                changes: [
                  "Hybrid architecture combining Claude's design with Llama's simplicity",
                  "GPT-4's functional patterns integrated with Gemini's performance optimizations",
                  "Advanced caching strategy from multiple agent suggestions",
                  "Comprehensive error handling combining best practices from all agents",
                  "Memory-efficient data structures with parallel processing capabilities",
                  "API redesign incorporating security hardening and rate limiting",
                  "Database optimization with intelligent query batching"
                ],
                metrics: {
                  Performance: "+65%",
                  Memory: "-45%",
                  "Code Quality": "A+",
                  Coverage: "98%",
                  Security: "A",
                  Maintainability: "95%"
                },
                analysis: "Evolutionary synthesis of 4 AI models through 3 iterative rounds. Combines architectural improvements from Claude, performance optimizations from Gemini, code simplification from Llama, and functional patterns from GPT-4. Each agent's strengths were preserved while weaknesses were eliminated through cross-pollination."
              }}
            />
            
            {/* Pros/Cons */}
            <div style={{ fontSize: '16px', marginTop: '35px' }}>
              <div style={{ marginBottom: '20px' }}>
                <strong style={{ color: '#4ECDC4', fontSize: '18px' }}>✓ Pros:</strong>
                <ul style={{ margin: '10px 0', paddingLeft: '28px', lineHeight: '2' }}>
                  <li>Maximum diversity</li>
                  <li>Iterative improvement</li>
                  <li>Best of all AI models</li>
                  <li>Reduced AI bias</li>
                  <li>Continuous evolution</li>
                </ul>
              </div>
              <div>
                <strong style={{ color: '#FF6B6B', fontSize: '18px' }}>✗ Cons:</strong>
                <ul style={{ margin: '10px 0', paddingLeft: '28px', lineHeight: '2' }}>
                  <li>Higher cost</li>
                  <li>Longer processing</li>
                  <li>Complex coordination</li>
                  <li>Agents still experimental</li>
                  <li>Potential for compounded errors</li>
                  <li>Requires careful validation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Add pulse animation */}
        <style jsx>{`
          @keyframes pulse {
            0% { opacity: 0.6; }
            50% { opacity: 1; }
            100% { opacity: 0.6; }
          }
        `}</style>

        {/* Comparison Table */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '16px',
          padding: '30px',
          overflow: 'auto'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '24px' }}>
            📊 Detailed Comparison
          </h3>
          
          <table style={{ 
            width: '100%', 
            fontSize: '14px',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr>
                <th style={{ 
                  textAlign: 'left', 
                  padding: '15px',
                  borderBottom: '2px solid rgba(255,255,255,0.2)',
                  fontWeight: '600',
                  fontSize: '16px'
                }}>
                  Feature
                </th>
                <th style={{ 
                  textAlign: 'center', 
                  padding: '15px',
                  borderBottom: '2px solid rgba(255,255,255,0.2)',
                  color: '#FF6B6B',
                  fontSize: '16px'
                }}>
                  Single Agent
                </th>
                <th style={{ 
                  textAlign: 'center', 
                  padding: '15px',
                  borderBottom: '2px solid rgba(255,255,255,0.2)',
                  color: '#4ECDC4',
                  fontSize: '16px'
                }}>
                  Artemis Classic
                </th>
                <th style={{ 
                  textAlign: 'center', 
                  padding: '15px',
                  borderBottom: '2px solid rgba(255,255,255,0.2)',
                  background: 'rgba(244, 81, 30, 0.1)',
                  color: '#F4511E',
                  fontSize: '16px'
                }}>
                  Multi-Agent Artemis
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: 'Human Control', basic: '❌ Minimal', current: '✅ Good', future: '✅ Maximum' },
                { feature: 'Solution Diversity', basic: '❌ None', current: '⚠️ Limited', future: '✅ High' },
                { feature: 'Codebase Understanding', basic: '⚠️ Partial', current: '✅ Complete', future: '✅ Multiple views' },
                { feature: 'Evolution/Improvement', basic: '❌ None', current: '✅ Yes', future: '✅ Advanced' },
                { feature: 'Processing Speed', basic: '✅ Fast', current: '⚠️ Moderate', future: '❌ Slower' },
                { feature: 'Cost', basic: '✅ Low', current: '⚠️ Medium', future: '❌ High' },
                { feature: 'Transparency', basic: '❌ Black box', current: '✅ High', future: '✅ Maximum' },
                { feature: 'Best For', basic: 'Quick fixes', current: 'Full optimization', future: 'Critical systems' }
              ].map((row, idx) => (
                <tr key={idx}>
                  <td style={{ 
                    padding: '12px 15px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    fontWeight: '500'
                  }}>
                    {row.feature}
                  </td>
                  <td style={{ 
                    padding: '12px 15px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'center'
                  }}>
                    {row.basic}
                  </td>
                  <td style={{ 
                    padding: '12px 15px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'center'
                  }}>
                    {row.current}
                  </td>
                  <td style={{ 
                    padding: '12px 15px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'center',
                    background: 'rgba(244, 81, 30, 0.05)'
                  }}>
                    {row.future}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Key Insight */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.1), rgba(69, 183, 209, 0.1))',
          borderRadius: '16px',
          padding: '30px',
          marginTop: '30px',
          border: '1px solid rgba(78, 205, 196, 0.3)'
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '20px', color: '#4ECDC4' }}>
            💡 Key Insight
          </h3>
          <p style={{ margin: 0, fontSize: '16px', lineHeight: '1.8' }}>
            The Multi-Agent Artemis approach treats AI agents like a diverse team of developers, 
            each creating their own feature branch. Artemis acts as the tech lead who reviews all 
            approaches, helps them evolve, and presents the best options based on your priorities. 
            This maximizes both AI capability and human control.
          </p>
        </div>

        {/* Example Use Cases */}
        <div style={{
          marginTop: '40px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '16px',
          padding: '30px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '24px' }}>
            🎯 Example Tasks for Multi-Agent Competition
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {Object.entries(taskDescriptions).map(([type, description]) => (
              <div key={type} style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h4 style={{ 
                  margin: '0 0 10px 0', 
                  fontSize: '18px',
                  textTransform: 'capitalize',
                  color: type === 'software' ? '#FF6B6B' :
                        type === 'ml' ? '#4ECDC4' :
                        type === 'trading' ? '#96CEB4' :
                        '#DDA0DD'
                }}>
                  {type === 'ml' ? 'Machine Learning' : 
                   type === 'data' ? 'Data Pipeline' : 
                   type.charAt(0).toUpperCase() + type.slice(1)}
                </h4>
                <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', opacity: 0.9 }}>
                  {description}
                </p>
              </div>
            ))}
          </div>
        </div>
          </>
        ) : activeTab === 'quantlib' ? (
          <QuantLibExamples />
        ) : activeTab === 'ml' ? (
          <MLExamples />
        ) : activeTab === 'intelligence' ? (
          <AIVariations />
        ) : (
          <LargeFileOptimization />
        )}
      </div>

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
        {modalContent}
      </Modal>
    </div>
  );
};

export default ArtemisApproaches; 